import React from 'react';

import { ArrowDropDownOutlined as ArrowDropDownIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  accordionDetailsClasses,
  accordionSummaryClasses,
  Box,
  Typography,
} from '@mui/material';

import { Field, useFormState } from '@hrlogics/react-components/form';
import { SelectFieldOption, SxProp } from '@hrlogics/react-helper-types';

import { AccessRestrictionMode, EmployerSimpleInfoFragment } from 'src/api';
import { AutocompleteField, RadioGroupField } from 'src/components/finalForm';
import { AutocompleteFieldResult } from 'src/hooks/forms';

import { AccessRestrictionModeFormField } from './constants';
import { AccessRestrictionModeFormValues, EmployeeBlacklistItem } from './types';

type AccessRestrictionModeFormPartProps = {
  renderFieldName: (fieldName: string) => string;
  selectedAccessRestrictionMode: AccessRestrictionMode;
  employersSelectProps?: AutocompleteFieldResult<EmployerSimpleInfoFragment>;
  employeesSelectProps?: AutocompleteFieldResult<EmployeeBlacklistItem>;
  employersOnly?: boolean;
};

export const AccessRestrictionModeFormPart: React.FC<AccessRestrictionModeFormPartProps> = ({
  renderFieldName,
  selectedAccessRestrictionMode,
  employersSelectProps,
  employeesSelectProps,
  employersOnly,
}) => {
  const { submitting } = useFormState<AccessRestrictionModeFormValues>({
    subscription: { submitting: true },
  });

  return (
    <Accordion disableGutters sx={accordionStyles} elevation={0}>
      <AccordionSummary sx={accordionStyles} expandIcon={<ArrowDropDownIcon />}>
        Set up Access Restriction lists
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" flexDirection="column" gap={2}>
          <Field
            label="Access Restriction Mode"
            name={renderFieldName(AccessRestrictionModeFormField.accessRestrictionMode)}
            component={RadioGroupField}
            fullWidth
            row
            options={accessRestrictionModeOptions}
            disabled={submitting}
          />

          {selectedAccessRestrictionMode === AccessRestrictionMode.Blacklist && (
            <React.Fragment>
              {employersOnly ? (
                <Typography variant="body1" color="text.secondary">
                  Using the dropdowns below, you can set up the employees that will not be visible
                  for this user. You can include Clients so the employees connected to those will
                  not be accessible.
                </Typography>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Using the dropdowns below, you can set up the employees that will not be visible
                  for this user. You can include SSNs to disable access for specific employees,
                  and/or include Clients so the employees connected to those will not be accessible.
                </Typography>
              )}

              <Field
                label="Client Blacklist"
                name={renderFieldName(AccessRestrictionModeFormField.employerBlacklist)}
                component={AutocompleteField}
                fullWidth
                multiple
                disabled={submitting}
                {...(employersSelectProps || { options: [] })}
              />

              <React.Activity mode={employersOnly ? 'hidden' : 'visible'}>
                <Field
                  label="Employee Blacklist"
                  name={renderFieldName(AccessRestrictionModeFormField.employeeBlacklist)}
                  component={AutocompleteField}
                  fullWidth
                  multiple
                  unfilterOptions
                  placeholder="Type full SSN to Search"
                  disabled={submitting}
                  {...(employeesSelectProps || { options: [] })}
                />
              </React.Activity>
            </React.Fragment>
          )}

          {selectedAccessRestrictionMode === AccessRestrictionMode.Whitelist && (
            <React.Fragment>
              {employersOnly ? (
                <Typography variant="body1" color="text.secondary">
                  Using the dropdowns below, you can define which employees will be visible to this
                  user. You can include specific Clients — only the employees associated with the
                  selected options will be accessible.
                </Typography>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  Using the dropdowns below, you can define which employees will be visible to this
                  user. You can include specific Clients — only the employees associated with the
                  selected options will be accessible.
                </Typography>
              )}

              <Field
                label="Client Whitelist"
                name={renderFieldName(AccessRestrictionModeFormField.employerWhitelist)}
                component={AutocompleteField}
                fullWidth
                multiple
                disabled={submitting}
                {...(employersSelectProps || { options: [] })}
              />
            </React.Fragment>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const accessRestrictionModeOptions: SelectFieldOption<AccessRestrictionMode>[] = [
  {
    label: 'Blacklist',
    value: AccessRestrictionMode.Blacklist,
  },
  {
    label: 'Whitelist',
    value: AccessRestrictionMode.Whitelist,
  },
];

const accordionStyles: SxProp = theme => ({
  border: 'none !important',

  '&::before': {
    content: 'none',
  },

  [`.${accordionSummaryClasses.content}`]: {
    minHeight: 0,
    margin: '0 !important',
    padding: 2,
  },
  [`.${accordionSummaryClasses.root}`]: {
    minHeight: '0 !important',
    margin: 0,
    paddingLeft: 0,
    paddingRight: 1,

    borderTop: '1px solid !important',
    borderBottom: '1px solid !important',
    borderColor: `${theme.palette.border?.main} !important`,
  },
  [`.${accordionDetailsClasses.root}`]: {
    padding: '16px 0 14px',
  },
});
